import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import { 
  ArrowLeft, 
  Filter, 
  Search, 
  Download, 
  TrendingUp, 
  Calendar,
  Users,
  DollarSign,
  CheckCircle,
  Clock,
  AlertCircle,
  Eye,
  Edit,
  Trash2
} from 'lucide-react';

const CoupleContributionsPage = () => {
  const { coupleId } = useParams();
  const navigate = useNavigate();
  
  const [coupleData, setCoupleData] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filtres
  const [filters, setFilters] = useState({
    year: '', // Pas de filtre par défaut pour voir toutes les années
    month: '',
    status: '',
    search: ''
  });
  
  // Statistiques
  const [statistics, setStatistics] = useState({
    totalContributions: 0,
    totalPaid: 0,
    totalPending: 0,
    paymentPercentage: 0,
    currentYearTotal: 0
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);

  // Années disponibles pour le filtre (initialisées avec les années prédéfinies)
  const [availableYears, setAvailableYears] = useState([2030, 2029, 2028, 2027, 2026, 2025, 2024, 2023]);

  useEffect(() => {
    fetchCoupleContributions();
  }, [coupleId, filters]);

  const fetchCoupleContributions = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('🔍 Fetching couple contributions for:', coupleId);
      console.log('📊 Filters:', filters);
      
      // Utiliser apiService qui gère automatiquement l'authentification
      const response = await apiService.get(`/contributions/couple/${encodeURIComponent(coupleId)}`, {
        params: filters
      });
      
      console.log('📥 API Response:', response);
      
      // apiService retourne déjà response.data
      const data = response.data || response;
      console.log('📥 Parsed data:', data);
      
      // L'API retourne directement les données (couple_data, contributions, statistics)
      // Pas besoin de vérifier data.success car l'API ne l'envoie pas
      
      if (data && data.couple_data && data.contributions) {
        setCoupleData(data.couple_data);
        
        // Créer un mapping user_id -> member pour enrichir les contributions
        const memberMap = {};
        if (data.couple_data.members && Array.isArray(data.couple_data.members)) {
          data.couple_data.members.forEach(member => {
            if (member.id) {
              memberMap[member.id] = member;
            }
          });
        }
        
        // Enrichir les contributions avec les informations des membres
        const enrichedContributions = (data.contributions || []).map(contribution => ({
          ...contribution,
          member: memberMap[contribution.user_id] || null
        }));
        
        setContributions(enrichedContributions);
        setStatistics(data.statistics || {
          totalContributions: 0,
          totalPaid: 0,
          totalPending: 0,
          paymentPercentage: 0,
          currentYearTotal: 0
        });
        
        // Générer les années disponibles (2023-2030 + années des contributions existantes)
        const contributionsList = enrichedContributions;
        const contributionYears = [...new Set(contributionsList.map(c => c.period_year))];
        const predefinedYears = [2023, 2024, 2026, 2027, 2028, 2029, 2030];
        const allYears = [...new Set([...predefinedYears, ...contributionYears])];
        setAvailableYears(allYears.sort((a, b) => b - a));
        
        console.log('✅ Data loaded successfully:', {
          coupleData: data.couple_data,
          contributionsCount: contributionsList.length,
          statistics: data.statistics,
          availableYears: allYears
        });
        
        // Debug: afficher quelques contributions pour comprendre la structure
        console.log('📊 Sample contributions:', contributionsList.slice(0, 3));
      } else {
        console.error('❌ Invalid response structure:', data);
        setError('Couple non trouvé ou données invalides');
      }
      
    } catch (err) {
      console.error('❌ Error fetching couple contributions:', err);
      console.error('❌ Error details:', {
        message: err.message,
        response: err.response,
        request: err.request,
        code: err.code
      });
      
      if (err.code === 'NETWORK_ERROR' || err.message === 'Network Error') {
        setError('Serveur API non disponible. Vérifiez que le serveur Laravel est démarré sur le port 8000.');
      } else if (err.response?.status === 404) {
        setError(`Couple "${decodeURIComponent(coupleId)}" non trouvé`);
      } else if (err.response?.status === 500) {
        setError('Erreur interne du serveur. Consultez les logs Laravel.');
      } else if (err.response) {
        setError(`Erreur API: ${err.response.status} - ${err.response.statusText}`);
      } else {
        setError(`Erreur de connexion: ${err.message || 'Impossible de joindre le serveur'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({
      year: '', // Réinitialiser sans filtre par défaut
      month: '',
      status: '',
      search: ''
    });
    setCurrentPage(1);
  };

  const handleExportContributions = () => {
    try {
      console.log('🔄 Début de l\'exportation des contributions...');
      console.log('📊 Filtres appliqués:', filters);
      console.log('📊 Contributions filtrées:', filteredContributions);
      
      if (filteredContributions.length === 0) {
        alert('Aucune contribution à exporter avec les filtres actuels.');
        return;
      }
      
      // Préparer les données pour l'export CSV
      const csvData = filteredContributions.map(contribution => {
        const member = contribution.member;
        return {
          'Couple': coupleData?.couple_name || coupleId,
          'Prénom': member?.first_name || 'N/A',
          'Nom': member?.last_name || 'N/A', 
          'Email': member?.email || 'N/A',
          'Téléphone': member?.phone || 'N/A',
          'Rôle': member?.role || 'N/A',
          'Année': contribution.period_year || '',
          'Mois': contribution.period_month ? getMonthName(contribution.period_month) : '',
          'Période': `${getMonthName(contribution.period_month)} ${contribution.period_year}`,
          'Montant': contribution.amount || 0,
          'Statut': getStatusLabel(contribution.status),
          'Date de paiement': contribution.payment_date ? formatDate(contribution.payment_date) : 'Non payé',
          'Méthode de paiement': contribution.payment_method || 'N/A',
          'Notes': contribution.notes || '',
          'Date de création': contribution.created_at ? formatDate(contribution.created_at) : ''
        };
      });

      // Créer le contenu CSV
      const headers = Object.keys(csvData[0]);
      const csvContent = [
        headers.join(','),
        ...csvData.map(row => headers.map(header => {
          const value = row[header];
          // Échapper les guillemets et entourer de guillemets si nécessaire
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(','))
      ].join('\n');

      // Créer le fichier et déclencher le téléchargement
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      // Nom du fichier avec informations du couple et date/heure
      const coupleName = (coupleData?.couple_name || coupleId).replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 19).replace(/[T:]/g, '-');
      const fileName = `contributions_${coupleName}_${dateStr}.csv`;
      
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log(`✅ Export réussi: ${csvData.length} contributions exportées pour le couple ${coupleData?.couple_name || coupleId}`);
      
    } catch (error) {
      console.error('❌ Erreur lors de l\'export:', error);
      alert('❌ Erreur lors de l\'export. Veuillez réessayer.');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'paid': { 
        bg: 'bg-green-100', 
        text: 'text-green-800', 
        icon: CheckCircle 
      },
      'pending': { 
        bg: 'bg-yellow-100', 
        text: 'text-yellow-800', 
        icon: Clock 
      },
      'overdue': { 
        bg: 'bg-red-100', 
        text: 'text-red-800', 
        icon: AlertCircle 
      }
    };

    const config = statusConfig[status] || statusConfig['pending'];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status.toUpperCase()}
      </span>
    );
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' XOF';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // Fonction utilitaire pour obtenir le nom du mois
  const getMonthName = (month) => {
    const monthNames = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    return monthNames[month - 1] || month;
  };

  // Fonction utilitaire pour obtenir le libellé du statut
  const getStatusLabel = (status) => {
    const statusLabels = {
      'paid': 'Payé',
      'pending': 'En attente', 
      'overdue': 'En retard'
    };
    return statusLabels[status] || status;
  };

  // Filtrer les contributions avec debug
  const filteredContributions = contributions.filter(contribution => {
    // Debug: afficher les données pour comprendre le problème
    if (filters.year && contribution.period_year) {
      console.log('🔍 Debug filtre année:', {
        filterYear: filters.year,
        filterYearType: typeof filters.year,
        contributionYear: contribution.period_year,
        contributionYearType: typeof contribution.period_year,
        comparison: contribution.period_year === parseInt(filters.year),
        parsedFilterYear: parseInt(filters.year)
      });
    }
    
    // Améliorer la logique de comparaison pour l'année
    const matchesYear = !filters.year || !contribution.period_year || 
      parseInt(contribution.period_year) === parseInt(filters.year);
    
    const matchesMonth = !filters.month || !contribution.period_month || 
      parseInt(contribution.period_month) === parseInt(filters.month);
    
    const matchesStatus = !filters.status || contribution.status === filters.status;
    
    const matchesSearch = !filters.search || 
      (contribution.member && (
        (contribution.member.first_name && contribution.member.first_name.toLowerCase().includes(filters.search.toLowerCase())) ||
        (contribution.member.last_name && contribution.member.last_name.toLowerCase().includes(filters.search.toLowerCase())) ||
        (contribution.member.email && contribution.member.email.toLowerCase().includes(filters.search.toLowerCase()))
      ));
    
    // Debug: résultat du filtrage pour chaque contribution
    if (filters.year) {
      console.log('🔍 Contribution filtrage:', {
        id: contribution.id,
        year: contribution.period_year,
        month: contribution.period_month,
        status: contribution.status,
        member: contribution.member?.first_name + ' ' + contribution.member?.last_name,
        matchesYear,
        matchesMonth,
        matchesStatus,
        matchesSearch,
        finalResult: matchesYear && matchesMonth && matchesStatus && matchesSearch
      });
    }
    
    return matchesYear && matchesMonth && matchesStatus && matchesSearch;
  });

  // Pagination
  const totalPages = Math.ceil(filteredContributions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedContributions = filteredContributions.slice(startIndex, startIndex + itemsPerPage);

  const months = [
    { value: '', label: 'Tous les mois' },
    { value: 1, label: 'Janvier' },
    { value: 2, label: 'Février' },
    { value: 3, label: 'Mars' },
    { value: 4, label: 'Avril' },
    { value: 5, label: 'Mai' },
    { value: 6, label: 'Juin' },
    { value: 7, label: 'Juillet' },
    { value: 8, label: 'Août' },
    { value: 9, label: 'Septembre' },
    { value: 10, label: 'Octobre' },
    { value: 11, label: 'Novembre' },
    { value: 12, label: 'Décembre' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des contributions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-white bg-opacity-80 backdrop-blur-sm rounded-2xl shadow-xl p-8 max-w-lg mx-auto">
            <div className="text-red-500 text-6xl mb-4">😞</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Oups !</h2>
            {/* <p className="text-gray-600 mb-4">{error}</p> */}
            <p className="text-gray-600 mb-4 text-justify">
              
              Pas de données disponibles.</p>

            
            {error.includes('Serveur API non disponible') && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-left text-sm">
                <h3 className="font-bold text-blue-800 mb-2">💡 Solution :</h3>
                <p className="text-blue-700 mb-2">Démarrez le serveur Laravel :</p>
                <code className="block bg-gray-800 text-green-400 p-2 rounded font-mono">
                  cd fsf_backend<br />
                  php artisan serve --port=8000
                </code>
              </div>
            )}
            
            {error.includes('non trouvé') && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-left text-sm">
                <h3 className="font-bold text-yellow-800 mb-2">🔍 Couples disponibles :</h3>
                <ul className="text-yellow-700 space-y-1">
                  {/* <li>• <code>/couple/Couple%20Martin/contributions</code></li>
                  <li>• <code>/couple/Couple%20Dubois/contributions</code></li>
                  <li>• <code>/couple/GOGO/contributions</code></li> */}
                </ul>
              </div>
            )}
            
            <div className="space-y-3">
              <button
                onClick={() => {
                  setError('');
                  fetchCoupleContributions();
                }}
                className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Réessayer
              </button>
              {/* <button
                onClick={() => {
                  window.open('/test-couple-api.html', '_blank');
                }}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                🔧 Tester l'API directement
              </button> */}
              <button
                onClick={() => navigate(-1)}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Retour
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
          <div className="flex items-center mb-4 lg:mb-0">
            <button
              onClick={() => navigate(-1)}
              className="mr-4 p-2 hover:bg-white hover:bg-opacity-50 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Contributions du couple  {coupleData?.couple_name || 'Couple'}
              </h1>
              <p className="text-gray-600 mt-1 text-justify">
                Gestion et suivi des cotisations du couple
              </p>
            </div>
          </div>
        </div>

        {/* Informations du couple */}
        {coupleData && (
          <div className="bg-white bg-opacity-80 backdrop-blur-sm rounded-2xl shadow-xl p-6 mb-8 border border-white border-opacity-20">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Users className="w-5 h-5 mr-2 text-purple-600" />
              Informations du Couple
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {coupleData.members && Array.isArray(coupleData.members) && coupleData.members.map((member, index) => (
                <div key={member.id || index} className="bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {member?.first_name || 'Prénom'} {member?.last_name || 'Nom'}
                      </h3>
                      <p className="text-gray-600 text-sm">{member?.email || 'Email non renseigné'}</p>
                      <p className="text-gray-600 text-sm">{member?.phone || 'Téléphone non renseigné'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Cotisation mensuelle</p>
                      <p className="font-bold text-purple-600">
                        {formatAmount(member?.contribution_monthly || 0)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-gray-600 text-justify">
                <strong>Date de mariage:</strong> {formatDate(coupleData.marriage_date)}
              </p>
              {coupleData.members && coupleData.members.length === 1 && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    ⚠️ <strong>Attention :</strong> Ce couple n'a qu'un seul membre enregistré. 
                    Pour un suivi complet des contributions du couple, assurez-vous d'ajouter le conjoint.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white bg-opacity-80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-white border-opacity-20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Contributions</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.totalContributions}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-purple-600" />
            </div>
          </div>
          
          <div className="bg-white bg-opacity-80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-white border-opacity-20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Payé</p>
                <p className="text-2xl font-bold text-green-600">{formatAmount(statistics.totalPaid)}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>
          
          <div className="bg-white bg-opacity-80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-white border-opacity-20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">En Attente</p>
                <p className="text-2xl font-bold text-yellow-600">{formatAmount(statistics.totalPending)}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
          </div>
          
          <div className="bg-white bg-opacity-80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-white border-opacity-20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Taux de Paiement</p>
                <p className="text-2xl font-bold text-blue-600">{statistics.paymentPercentage}%</p>
              </div>
              <DollarSign className="w-8 h-8 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Filtres */}
        <div className="bg-white bg-opacity-80 backdrop-blur-sm rounded-2xl shadow-xl p-6 mb-8 border border-white border-opacity-20">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Filter className="w-5 h-5 mr-2 text-purple-600" />
            Filtres
            {(filters.year || filters.month || filters.status || filters.search) && (
              <span className="ml-2 text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                Filtres actifs
              </span>
            )}
          </h2>
          
          {/* Debug des filtres - à supprimer en production */}
          {/* {process.env.NODE_ENV === 'development' && (
            <div className="mb-4 p-3 bg-gray-100 rounded text-xs">
              <strong>Debug filtres:</strong> {JSON.stringify(filters)} | 
              <strong> Contributions total:</strong> {contributions.length} | 
              <strong> Filtrées:</strong> {filteredContributions.length}
            </div>
          )} */}
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Recherche */}
            <div className="relative">
              <label className="block text-xs font-medium text-gray-700 mb-1">Recherche</label>
              <Search className="absolute left-3 top-8 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un membre..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Année */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Année</label>
              <select
                value={filters.year}
                onChange={(e) => handleFilterChange('year', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">Toutes les années</option>
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            {/* Mois */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Mois</label>
              <select
                value={filters.month}
                onChange={(e) => handleFilterChange('month', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {months.map(month => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
            </div>

            {/* Statut */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Statut</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">Tous les statuts</option>
                <option value="paid">Payé</option>
                <option value="pending">En attente</option>
                <option value="overdue">En retard</option>
              </select>
            </div>

            {/* Reset */}
            <div className="flex items-end">
              <button
                onClick={resetFilters}
                className="w-full px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </div>

        {/* Tableau des contributions */}
        <div className="bg-white bg-opacity-80 backdrop-blur-sm rounded-2xl shadow-xl border border-white border-opacity-20 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 sm:mb-0">
                Contributions ({filteredContributions.length})
              </h2>
              <div className="flex space-x-2">
                <button 
                  onClick={handleExportContributions}
                  disabled={filteredContributions.length === 0}
                  className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-sm font-medium"
                  title={filteredContributions.length === 0 ? "Aucune contribution à exporter" : `Exporter ${filteredContributions.length} contribution(s)`}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Exporter ({filteredContributions.length})
                </button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Membre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Période
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date de paiement
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedContributions.length > 0 ? paginatedContributions.map((contribution) => (
                  <tr key={contribution.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-purple-600">
                            {contribution.member?.first_name?.[0] || 'U'}{contribution.member?.last_name?.[0] || 'U'}
                          </span>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {contribution.member?.first_name || 'Utilisateur'} {contribution.member?.last_name || 'Inconnu'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {months.find(m => m.value === contribution.period_month)?.label} {contribution.period_year}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatAmount(contribution.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(contribution.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {contribution.payment_date ? formatDate(contribution.payment_date) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button className="text-purple-600 hover:text-purple-900 p-1 rounded">
                          {/* <Eye className="h-4 w-4" /> */}
                          {getStatusBadge(contribution.status) ? contribution.status === 'paid' ? 'Aucune action requise' : 'Pas encore confirmé' : ''}
                        
                        </button>
                        

                        {/* <button className="text-blue-600 hover:text-blue-900 p-1 rounded">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button className="text-red-600 hover:text-red-900 p-1 rounded">
                          <Trash2 className="h-4 w-4" />
                        </button> */}
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <div className="text-gray-400">
                        <div className="text-6xl mb-4">📊</div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune contribution trouvée</h3>
                        <p className="text-gray-500 mb-4 text-justify">
                          Ce couple n'a pas encore de cotisations enregistrées{filters.year ? ` pour l'année ${filters.year}` : ''}.
                        </p>
                        {Object.values(filters).some(f => f) && (
                          <button
                            onClick={resetFilters}
                            className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                          >
                            Réinitialiser les filtres
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Affichage {startIndex + 1} à {Math.min(startIndex + itemsPerPage, filteredContributions.length)} sur {filteredContributions.length} contributions
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50"
                  >
                    Précédent
                  </button>
                  
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`px-3 py-1 text-sm border rounded ${
                        currentPage === i + 1
                          ? 'bg-purple-600 text-white border-purple-600'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoupleContributionsPage;